<template>
  <CustomModal
    source="add_external_domain"
    v-model:visible="isVisible"
    :title="modalTitle"
    width="480px"
    :close_on_outside_click="stage === 'form' && !isSubmitting"
    @close="handleClose"
  >
    <div class="flex flex-col gap-4">
      <template v-if="stage === 'form'">
        <div class="form_col">
          <label for="add_external_domain_input" class="text-xs secondary_color">
            {{ COPY.DOMAIN_LABEL }}
          </label>
          <a-input
            id="add_external_domain_input"
            v-model:value="domainInput"
            class="input_medium"
            :placeholder="COPY.DOMAIN_PLACEHOLDER"
            :disabled="isSubmitting"
            allow-clear
            @pressEnter="submitDomain"
          />
          <span class="text-xs secondary_color">{{ COPY.DOMAIN_HELP }}</span>
          <span v-if="fieldError" class="error-message text-xs">
            {{ fieldError }}
          </span>
        </div>
      </template>

      <template v-else-if="stage === 'dns' && record">
        <DnsInstructionsPanel
          :record="record"
          :started-at="startedAt"
        />
      </template>
    </div>

    <template #footer>
      <div class="flex gap-2">
        <a-button
          id="add_external_domain_cancel"
          class="secondary_medium"
          :disabled="isSubmitting"
          @click="handleClose"
        >
          {{ isParked ? "Done" : "Close" }}
        </a-button>
        <a-button
          v-if="stage === 'form'"
          id="add_external_domain_submit"
          class="primary_medium"
          :loading="isSubmitting"
          :disabled="!normalizedDomain"
          @click="submitDomain"
        >
          Continue
        </a-button>
        <a-button
          v-else-if="stage === 'dns' && isFailed && !isViewMode"
          id="add_external_domain_retry"
          class="primary_medium"
          @click="resetToForm"
        >
          Try again
        </a-button>
      </div>
    </template>
  </CustomModal>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount } from "vue";
import { message } from "ant-design-vue";
import CustomModal from "@/src/components/CustomModal.vue";
import DnsInstructionsPanel from "@/src/components/external_domain/DnsInstructionsPanel.vue";
import ExternalDomainService from "@/src/api/ExternalDomainService.js";
import { isValidDomain } from "@/src/utils/utils.js";
import {
  EXTERNAL_DOMAIN_STATUS,
  EXTERNAL_DOMAINS_COPY as COPY,
  POLL_INTERVAL_MS,
} from "@/src/constants/externalDomainsConstants.js";

const props = defineProps({
  visible: { type: Boolean, default: false },
  initialRecord: { type: Object, default: null },
});

const emit = defineEmits(["update:visible", "created"]);

const isVisible = computed({
  get: () => props.visible,
  set: (val) => emit("update:visible", val),
});

const stage = ref("form");
const domainInput = ref("");
const fieldError = ref("");
const isSubmitting = ref(false);
const record = ref(null);
const startedAt = ref(Date.now());
let pollTimer = null;

const normalizedDomain = computed(() =>
  domainInput.value
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/\/+$/, "")
    .replace(/^www\./, ""),
);

const isParked = computed(
  () => record.value?.status === EXTERNAL_DOMAIN_STATUS.PARKED,
);

const isFailed = computed(
  () => record.value?.status === EXTERNAL_DOMAIN_STATUS.FAILED,
);

const isViewMode = computed(() => !!props.initialRecord);

const modalTitle = computed(() =>
  isViewMode.value ? COPY.MODAL_TITLE_VIEW : COPY.MODAL_TITLE,
);

const resetState = () => {
  stage.value = "form";
  domainInput.value = "";
  fieldError.value = "";
  record.value = null;
  isSubmitting.value = false;
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
};

const resetToForm = () => {
  resetState();
};

const handleClose = () => {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  const createdAnything = !!record.value;
  isVisible.value = false;
  if (createdAnything) {
    emit("created");
  }
};

const submitDomain = async () => {
  fieldError.value = "";
  const host = normalizedDomain.value;
  if (!isValidDomain(host)) {
    fieldError.value = COPY.DOMAIN_INVALID;
    return;
  }

  isSubmitting.value = true;
  try {
    const response = await ExternalDomainService.createDomain(host);
    record.value = response;
    startedAt.value = Date.now();
    stage.value = "dns";
    startPolling();
    emit("created");
  } catch (error) {
    const status = error?.response?.status;
    if (status === 409) {
      fieldError.value = COPY.DOMAIN_TAKEN;
    } else if (status === 400) {
      fieldError.value =
        error?.response?.data?.error || COPY.DOMAIN_INVALID;
    } else {
      fieldError.value = COPY.GENERIC_ERROR;
    }
  } finally {
    isSubmitting.value = false;
  }
};

const startPolling = () => {
  if (pollTimer) clearInterval(pollTimer);
  pollTimer = setInterval(async () => {
    if (!record.value?.id) return;
    try {
      const fresh = await ExternalDomainService.getDomainStatus(record.value.id);
      record.value = fresh;
      if (
        fresh?.status === EXTERNAL_DOMAIN_STATUS.PARKED ||
        fresh?.status === EXTERNAL_DOMAIN_STATUS.ACTIVE ||
        fresh?.status === EXTERNAL_DOMAIN_STATUS.FAILED
      ) {
        clearInterval(pollTimer);
        pollTimer = null;
        emit("created");
        if (fresh.status === EXTERNAL_DOMAIN_STATUS.PARKED) {
          message.success("Domain is ready");
        }
      }
    } catch (error) {
      // keep polling; one failed refresh shouldn't kill the loop
    }
  }, POLL_INTERVAL_MS);
};

watch(
  () => props.visible,
  (val) => {
    if (!val) {
      resetState();
    }
  },
);

if (props.initialRecord) {
  record.value = props.initialRecord;
  stage.value = "dns";
  startedAt.value = props.initialRecord.created_at
    ? new Date(props.initialRecord.created_at).getTime()
    : Date.now();
  const status = props.initialRecord.status;
  const isTerminal =
    status === EXTERNAL_DOMAIN_STATUS.PARKED ||
    status === EXTERNAL_DOMAIN_STATUS.ACTIVE ||
    status === EXTERNAL_DOMAIN_STATUS.FAILED;
  if (!isTerminal) {
    startPolling();
  }
}

onBeforeUnmount(() => {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
});
</script>
